// Rejected goods step 2 (2026-10-25; approved mockup AUMPidbNn2tJqJ4ujXtcPv, "Returns & credits" 8): one Settle action everywhere.
// A return: credit note / refund (cash, InstaPay, transfer into ADIB) / taken off another invoice / replaced instead (the later delivery).
// A discount settles the same way without "replaced instead"; a discount the vendor was not paid for yet (Ahmed, 24 Oct) offers "Paid less"
// first (step 3's Record payment does it by itself).
// Step 4a (Ahmed, 25 Oct; mockup DyrAbPJDgtF8t6mjFfytkv "Door & returns" 4): settling is money only, a return never touches the PO (D2
// withdrawn), old returns included. A short close the vendor was paid for settles by credit note, refund or another invoice. A later return
// of goods from a PO received in full may be "replaced instead" without a delivery to pick: the note then says how the new goods came.
// Step 4j (Ahmed 2 Nov, real-week walkthrough): money paid over on a PO settles the same way, in parts if it comes back in parts. Taken off another PO lowers what that PO owes.
import React, { useEffect, useState } from 'react';
import { Alert, DatePicker, Input, InputNumber, Modal, Radio, Select, Spin, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { CreditKind, getSettleInfo, settleCase, SettleHow, SettleInfo } from '../../api';
import { useCompactLayout } from '../../hooks/useCompactLayout';
import { money, OutcomeCard } from '../receiving/shared';

export interface SettleTarget { kind: CreditKind; id: number }

const lab: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, color: '#5f6672', minWidth: 0 };
const text: React.CSSProperties = { fontSize: 12.5, color: '#5f6672' };
const req = <span style={{ color: '#cf1322' }}>* </span>;
const d = (s: string | null | undefined) => (s ? dayjs(s).format('D MMM') : '');

const SettleWindow: React.FC<{ target: SettleTarget | null; onClose: () => void; onSaved: () => void }> = ({ target, onClose, onSaved }) => {
  const compact = useCompactLayout();
  const [info, setInfo] = useState<SettleInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [how, setHow] = useState<SettleHow | null>(null);
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [amount, setAmount] = useState<number | null>(null);
  const [reference, setReference] = useState('');
  const [refundMethod, setRefundMethod] = useState<'cash' | 'instapay' | 'transfer' | null>(null);
  const [otherPo, setOtherPo] = useState('');
  const [otherInvoice, setOtherInvoice] = useState('');
  const [replacedBy, setReplacedBy] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!target) { setInfo(null); return; }
    setHow(null); setDate(dayjs()); setAmount(null); setReference(''); setRefundMethod(null); setOtherPo(''); setOtherInvoice(''); setReplacedBy(null); setNote('');
    setLoading(true);
    getSettleInfo(target.kind, target.id)
      .then((i) => { setInfo(i); setAmount(i.case.owed || null); if (i.case.state === 'pay_less') setHow('paid_less'); })
      .catch((e) => { message.error(e?.response?.data?.error || 'Could not open the case'); onClose(); })
      .finally(() => setLoading(false));
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps

  const c = info?.case;
  const vendor = c?.vendor?.name || 'the vendor';
  const qtyText = c ? `${c.qty.toLocaleString('en-US', { maximumFractionDigits: 2 })}${c.unit ? ` ${c.unit}` : ''}` : '';
  const options: Array<{ value: SettleHow; label: string; hint: string }> = !c ? [] : [
    ...(c.state === 'pay_less' ? [{ value: 'paid_less' as SettleHow, label: 'Paid less', hint: `We paid ${vendor} the discount less.` }] : []),
    { value: 'credit_note', label: 'Credit note', hint: 'Number, date and amount.' },
    { value: 'refund', label: 'Refund', hint: 'Amount, and how it came back: cash, InstaPay or transfer into ADIB, with its reference.' },
    { value: 'other_invoice', label: 'Taken off another invoice', hint: 'Which invoice or PO, and the amount.' },
    ...(c.kind === 'return' ? [{ value: 'replaced' as SettleHow, label: 'Replaced instead', hint: c.source === 'accepted' ? 'The vendor brought new ones: pick the delivery, or say how they came.' : 'Pick the delivery that brought the new goods.' }] : []),
  ];
  // A later return (step 4a) can be replaced without a receipt: the PO was usually received in full, so the new goods have none.
  const replacedFree = c?.source === 'accepted' && !replacedBy;
  // Never more than the case is owed (Ahmed, 26 Oct); old returns keep their stored values, so they take what the vendor really gave.
  const tooMuch = !!c && !c.old && how !== 'replaced' && (amount || 0) > c.owed + 0.005;
  const ok = !!how && !!date && (
    how === 'replaced' ? (!!replacedBy || (replacedFree && !!note.trim()))
      : !!(amount && amount > 0) && !tooMuch && (how !== 'credit_note' || !!reference.trim()) && (how !== 'refund' || !!refundMethod) && (how !== 'other_invoice' || !!(otherPo.trim() || otherInvoice.trim()))
  );

  const save = async () => {
    if (!target || !how || !date) return;
    setSaving(true);
    try {
      await settleCase(target.kind, target.id, {
        how, date: how === 'replaced' && replacedBy ? dayjs(info!.deliveries.find((x) => x.grnHeaderId === replacedBy)?.receivedOn).format('YYYY-MM-DD') : date.format('YYYY-MM-DD'),
        amount: how === 'replaced' ? undefined : amount ?? undefined, reference: reference.trim() || undefined,
        refundMethod: how === 'refund' ? refundMethod ?? undefined : undefined,
        otherPoId: how === 'other_invoice' ? otherPo.trim() || undefined : undefined, otherInvoice: how === 'other_invoice' ? otherInvoice.trim() || undefined : undefined,
        replacedByGrnId: how === 'replaced' ? replacedBy ?? undefined : undefined, note: note.trim() || undefined,
      });
      message.success(c?.kind === 'overpaid' ? `${c.po?.poId || 'PO'} money settled` : c?.kind === 'invoice' ? `Invoice ${c.code || ''} money settled`.replace('  ', ' ') : `${c?.code || (c?.kind === 'short_close' ? 'Short close' : 'Discount')} settled`);
      onSaved();
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Could not save the settlement');
    } finally {
      setSaving(false);
    }
  };

  const dateField = (label = 'Date') => (
    <label style={lab}><span>{req}{label}</span>
      <DatePicker size="small" value={date} onChange={setDate} format="D MMM YYYY" disabledDate={(x) => x.isAfter(dayjs(), 'day')} inputReadOnly={compact} style={{ width: '100%' }} />
    </label>
  );
  const amountField = (label = 'Amount (EGP)') => (
    <label style={lab}><span>{req}{label}</span>
      <InputNumber size="small" min={0} precision={2} value={amount} onChange={(v) => setAmount(v == null ? null : Number(v))} status={tooMuch ? 'error' : undefined} inputMode="decimal" style={{ width: '100%' }} />
      {tooMuch && <span style={{ color: '#cf1322', fontSize: 12 }}><bdi>{vendor}</bdi> owes {money(c!.owed)} on this case; the amount cannot be more.</span>}
    </label>
  );
  const fields = (o: SettleHow) => {
    const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 8, marginTop: 6 };
    if (o === 'credit_note') return (
      <div style={grid} onClick={(e) => e.stopPropagation()}>
        <label style={lab}><span>{req}Number</span><Input size="small" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. CN-4471" maxLength={100} /></label>
        {dateField()}{amountField()}
      </div>
    );
    if (o === 'refund') return (
      <div style={grid} onClick={(e) => e.stopPropagation()}>
        {amountField()}{dateField()}
        <div style={{ ...lab, gridColumn: '1 / -1' }}><span>{req}How it came back</span>
          <Radio.Group size="small" optionType="button" value={refundMethod} onChange={(e) => setRefundMethod(e.target.value)}
            options={[{ value: 'cash', label: 'Cash' }, { value: 'instapay', label: 'InstaPay' }, { value: 'transfer', label: 'Transfer into ADIB' }]} />
        </div>
        <label style={{ ...lab, gridColumn: '1 / -1' }}><span>Reference</span><Input size="small" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="InstaPay / transfer reference" maxLength={100} /></label>
      </div>
    );
    if (o === 'other_invoice') return (
      <div style={grid} onClick={(e) => e.stopPropagation()}>
        <label style={lab}><span>{req}PO</span><Input size="small" value={otherPo} onChange={(e) => setOtherPo(e.target.value)} placeholder={`${vendor}'s PO, e.g. PO-000740`} maxLength={20} /></label>
        <label style={lab}><span>or invoice</span><Input size="small" value={otherInvoice} onChange={(e) => setOtherInvoice(e.target.value)} placeholder="Invoice number" maxLength={100} /></label>
        {amountField()}{dateField()}
        {otherPo.trim() && <div style={{ ...text, gridColumn: '1 / -1' }}>What {otherPo.trim().toUpperCase()} still owes goes down by this amount.</div>}
      </div>
    );
    if (o === 'replaced') return (
      <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 8 }} onClick={(e) => e.stopPropagation()}>
        {info?.deliveries.length ? (
          <Select size="small" style={{ width: '100%' }} placeholder="The delivery that brought the new goods" value={replacedBy ?? undefined} onChange={setReplacedBy} allowClear={c?.source === 'accepted'}
            options={info.deliveries.map((x) => ({ value: x.grnHeaderId, label: `${x.grnId} · ${d(x.receivedOn)} · ${x.accepted} accepted` }))} />
        ) : <div style={text}>No delivery on this PO line came after the return{c?.source === 'accepted' ? '. Say in the note how the new goods came back.' : ' yet.'}</div>}
        {replacedFree && dateField('Came back on')}
      </div>
    );
    return <div style={grid} onClick={(e) => e.stopPropagation()}>{amountField('Paid less (EGP)')}{dateField('Paid on')}</div>;
  };

  const closeNote = !c ? null
    : c.kind === 'discount' ? 'The goods were kept, so nothing changes on the PO.'
      : c.kind === 'short_close' ? <>The line was short-closed at the door. <b>{c.po?.poId || 'The PO'} does not change</b>; only money comes back.</>
        : c.kind === 'overpaid' ? <><bdi>{vendor}</bdi> was paid more than <b>{c.po?.poId || 'the PO'}</b> is worth ({c.reason}). <b>The PO does not change</b>; only money comes back. If it was typed wrong, correct the payment instead.</>
        : c.kind === 'invoice' ? <>The invoice asked more than the PO and it was paid: the vendor&apos;s mistake. <b>{c.po?.poId || 'The PO'} does not change</b>; only money comes back.</>
          : <>The PO stays <b>{c.po?.status || 'as it is'}</b>. Nothing on its lines changes.</>;

  return (
    <Modal
      open={!!target}
      title={c ? <div style={{ paddingInlineEnd: 28 }}>Settle {c.kind === 'overpaid' ? `money paid over · ${c.po?.poId || ''} ·` : c.kind === 'invoice' ? `invoice ${c.code || ''} · line ${c.lineNo ?? '-'} ·` : <>{c.code || (c.kind === 'short_close' ? 'short close' : 'discount')} · {qtyText}</>} <bdi>{c.item.description}</bdi> · <bdi>{vendor}</bdi> · EGP {money(c.owed)}</div> : 'Settle'}
      onCancel={onClose}
      onOk={save}
      okText="Save settlement"
      okButtonProps={{ disabled: !ok, loading: saving }}
      maskClosable={false}
      width={compact ? 520 : 680}
      destroyOnClose
    >
      {loading || !c ? <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {c.old && <div style={text}>Recorded before the new receiving: its value stays as recorded (EGP {money(c.owed)}).</div>}
          <div>
            <div id="settle-how" style={{ fontWeight: 600, marginBottom: 6 }}>{req}How did <bdi>{vendor}</bdi> settle it?</div>
            <div role="radiogroup" aria-labelledby="settle-how" style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
              {options.map((o) => (
                <OutcomeCard key={o.value} label={o.label} on={how === o.value} onPick={() => setHow(o.value)}>
                  {how === o.value ? fields(o.value) : <div style={text}>{o.hint}</div>}
                </OutcomeCard>
              ))}
            </div>
          </div>
          {closeNote && <Alert type="info" showIcon message={<span style={{ fontSize: 13 }}>{closeNote}</span>} style={{ margin: 0 }} />}
          <label style={lab}>
            <span>{how === 'replaced' && replacedFree ? <>{req}How the new goods came back</> : 'Note'}</span>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={how === 'replaced' && replacedFree ? 'Who brought them, when (required)' : 'Optional'} maxLength={500} />
          </label>
        </div>
      )}
    </Modal>
  );
};

export default SettleWindow;
